import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItem } from '../navConfig';

interface UseNavigationProps {
  isMobile?: boolean;
  setSidebarOpen?: (value: boolean) => void;
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const useNavigation = ({
  isMobile = false,
  setSidebarOpen,
  setExpandedItems,
}: UseNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const toggleExpanded = useCallback(
    (label: string) => {
      setExpandedItems(prev => ({ ...prev, [label]: !prev[label] }));
    },
    [setExpandedItems]
  );

  const closeExpanded = useCallback(
    (label: string) => {
      setExpandedItems(prev => ({ ...prev, [label]: false }));
    },
    [setExpandedItems]
  );

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobile && setSidebarOpen) {
        setSidebarOpen(false);
      }
    },
    [navigate, isMobile, setSidebarOpen]
  );

  const isActive = useCallback(
    (item: NavItem): boolean => {
      if (item.path) {
        // Exact match or path with trailing content starting with /
        const pathname = location.pathname;
        if (pathname === item.path) return true;
        if (pathname.startsWith(item.path + '/')) return true;
      }
      if (item.children) {
        return item.children.some(child => {
          const pathname = location.pathname;
          return pathname === child.path || pathname.startsWith(child.path + '/');
        });
      }
      return false;
    },
    [location.pathname]
  );

  const isChildActive = useCallback(
    (path: string): boolean => {
      const pathname = location.pathname;
      return pathname === path || pathname.startsWith(path + '/');
    },
    [location.pathname]
  );

  const currentPath = useMemo(() => location.pathname, [location.pathname]);

  return {
    toggleExpanded,
    closeExpanded,
    handleNavigate,
    isActive,
    isChildActive,
    currentPath,
  };
};
